import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth/types';
import { useListMemberships } from 'src/features/membership/api/useListMemberships';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useListMembershipTypes } from 'src/features/membershipType/api/useListMembershipTypes';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { PATH } from 'src/features/navigation/utils/constants';
import { useListActiveStays } from 'src/features/stay/api/useListActiveStays';
import { ActiveStay } from 'src/features/stay/types';
import ValidationsSidePanel from 'src/features/validations/components/ValidationsSidePanel';
import { CellSidePanelActions } from 'src/features/visitor/components/cell/CellSidePanelActions';
import { visitorCategoryBadgeOptions } from 'src/features/visitor/utils/constants';
import { visitorMessages } from 'src/features/visitor/utils/messages';
import { useListZones } from 'src/features/zone/api/useListZones';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import SidePanel from 'src/registry/components/sidePanel/SidePanel';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellAction } from 'src/registry/components/table/cells/CellAction';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import { useSingleRowSelection } from 'src/registry/hooks/useSingleRowSelection';
import useSorting from 'src/registry/hooks/useSorting';

const columnHelper = createColumnHelper<ActiveStay>();
const ACTIVE_STAYS_PANEL_NAME = 'active-stays-panel';

const sortingOptions = { initialSortingState: [{ id: 'entry_time', desc: true }] };

export default function ValidationTable() {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { queryFiltersOptions, ...filters } = useFilters();

    const [searchMembership, setSearchMembership] = React.useState<string>('');
    const [searchMembershipType, setSearchMembershipType] = React.useState<string>('');

    const accessVisitors = useHasPermissions([PERMISSION.ACCESS_VISITORS]);

    const { rowSelectionState, clearSelection, setRowSelectionState } = useSingleRowSelection();

    const singleRowSelection = React.useCallback(
        (rowId: string) => {
            setRowSelectionState({ [rowId]: true });
        },
        [setRowSelectionState],
    );

    const { data: listMemberships } = useListMemberships({
        parkingId,
        search: searchMembership,
        config: { enabled: filters.columnFiltersShowDropdownState.includes('membership') },
    });

    const { data: listMembershipTypes } = useListMembershipTypes({
        parkingId,
        search: searchMembershipType,
        config: { enabled: filters.columnFiltersShowDropdownState.includes('membership_type') },
    });

    const {
        data: activeStaysData,
        isError,
        isLoading,
    } = useListActiveStays({
        parkingId,
        ...queryPaginationOptions,
        ...querySearchOptions,
        ...querySortingOptions,
        ...queryFiltersOptions,
    });

    const { data: listZonesData } = useListZones({ parkingId });

    const filteredEmptyTranslations = React.useMemo(() => {
        return listZonesData?.results?.filter((zone) => zone.translations.length > 0);
    }, [listZonesData?.results]);

    const zoneFilterOptions = React.useMemo(
        () =>
            filteredEmptyTranslations?.map((zone) => {
                const translatedZone = zone.translations.find((z) => z.language === locale);

                return { value: zone.id, label: translatedZone?.name };
            }),
        [filteredEmptyTranslations, locale],
    );

    const membershipsFilterOptions = React.useMemo(
        () =>
            listMemberships?.results?.map((membership) => {
                return { value: membership.id, label: membership?.name };
            }),
        [listMemberships?.results],
    );

    const membershipTypesFilterOptions = React.useMemo(
        () =>
            listMembershipTypes?.results?.map((type) => {
                const translatedType = type.translations.find((t) => t.language === locale);

                return { value: type.id, label: translatedType?.name };
            }),
        [listMembershipTypes?.results, locale],
    );

    const [defaultDateFilter] = React.useState(() => {
        const now = Date.now();

        return {
            afterDate: new Date(now - 30 * 24 * 60 * 60 * 1000),
            beforeDate: new Date(),
        };
    });

    const columns = [
        columnHelper.accessor('ref', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellAction {...ctx} sidePanelName={ACTIVE_STAYS_PANEL_NAME} singleRowSelection={singleRowSelection} />
            ),
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.stayId),
            },
        }),
        columnHelper.accessor('main_identifier.value', {
            header: DataTableColumnHeader,
            cell: (ctx) => {
                return (
                    <CellLinkWithIcon
                        {...ctx}
                        to={`/${PATH.VISITORS}/visitor/${ctx.row.original.visitor.id}`}
                        hasPermissions={accessVisitors}
                    />
                );
            },
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.visitor),
            },
        }),
        columnHelper.accessor('additional_identifiers.value', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: ({ row }) => {
                const identifiers = row.original.additional_identifiers.map((identifier) => identifier.value);
                const mainIdentifier = row.original.main_identifier.value;
                if (mainIdentifier) identifiers.unshift(mainIdentifier);

                return identifiers.length > 0 ? identifiers.join(', ') : '-';
            },
            meta: {
                title: formatMessage(visitorMessages.identifiers),
            },
        }),
        columnHelper.accessor('visitor_owner.name', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(visitorMessages.owner),
            },
        }),
        columnHelper.accessor('period_visitor_category', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (props) => <CellBadge badgeOptions={visitorCategoryBadgeOptions} {...props} />,
            meta: {
                title: formatMessage(visitorMessages.type),
                badgeOptions: visitorCategoryBadgeOptions,
            },
        }),
        columnHelper.accessor('period_zone', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return (
                    <div className="whitespace-nowrap">
                        {row.original.period_zone?.translations?.find((zone) => zone.language === locale)?.name ?? '-'}
                    </div>
                );
            },
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.parkingZone),
                options: zoneFilterOptions,
                inputType: INPUT_TYPE.MULTI_SELECT,
            },
            id: 'zones',
        }),
        columnHelper.accessor('entry_time', {
            header: DataTableColumnHeader,
            cell: CellDatetime,
            enableHiding: true,
            enableSorting: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(visitorMessages.enteredAt),
                inputType: INPUT_TYPE.DATE,
                dateFilter: defaultDateFilter,
                inlineDateFilter: true,
            },
        }),
        columnHelper.accessor('entry_gateway', {
            header: DataTableColumnHeader,
            cell: ({ row }) => {
                return row.original.entry_gateway?.translations?.find((zone) => zone.language === locale)?.name ?? '-';
            },
            enableHiding: true,
            meta: {
                title: formatMessage(visitorMessages.entryPoint),
            },
        }),
        columnHelper.display({
            id: 'actions',
            cell: (props) => (
                <CellSidePanelActions
                    {...props}
                    sidePanelName={ACTIVE_STAYS_PANEL_NAME}
                    singleRowSelection={singleRowSelection}
                />
            ),
        }),
        columnHelper.accessor('ref', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(membershipMessages.memberships),
                options: membershipsFilterOptions,
                inputType: INPUT_TYPE.SEARCH,
            },
            id: 'membership',
        }),
        columnHelper.accessor('ref', {
            enableColumnFilter: true,
            meta: {
                title: formatMessage(membershipTypeMessages.membershipTemplates),
                options: membershipTypesFilterOptions,
                inputType: INPUT_TYPE.SEARCH,
            },
            id: 'membership_type',
        }),
    ];

    const selectedStayId = React.useMemo(() => {
        const firstSelected = Object.keys(rowSelectionState)[0];
        if (!activeStaysData || !firstSelected) return;
        return activeStaysData.results.find((row) => row.id === firstSelected)?.id;
    }, [activeStaysData, rowSelectionState]);

    const searchPlaceholder = `${formatMessage(visitorMessages.stayId)}, ${formatMessage(visitorMessages.visitor)}, ${formatMessage(visitorMessages.identifiers)}, ${formatMessage(visitorMessages.owner)}`;

    return (
        <>
            <DataTable<ActiveStay, any>
                columns={columns}
                data={activeStaysData}
                pagination={pagination}
                sorting={sorting}
                filters={filters}
                search={search}
                semantics={{
                    title: formatMessage(visitorMessages.activeStays),
                    entryName: formatMessage(visitorMessages.stay).toLowerCase(),
                    entryNames: formatMessage(visitorMessages.stays).toLowerCase(),
                    placeholder: searchPlaceholder,
                    initialColumnVisibility: {
                        membership: false,
                        membership_type: false,
                    },
                    sidePanelName: ACTIVE_STAYS_PANEL_NAME,
                }}
                isError={isError}
                isLoading={isLoading}
                isMultiSelect
                enableSearchParams={false}
                getRowId={(row) => row.id.toString()}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                }}
                onFilterSearch={(columnId, value) => {
                    if (columnId === 'membership') {
                        setSearchMembership(value);
                    }
                    if (columnId === 'membership_type') {
                        setSearchMembershipType(value);
                    }
                }}
            />
            <SidePanel onClose={clearSelection} name={ACTIVE_STAYS_PANEL_NAME}>
                <ValidationsSidePanel selectedStayId={selectedStayId} />
            </SidePanel>
        </>
    );
}
